<template>
  <div
    :draggable="draggable"
    :style="style"
    :class="[
      {
        [classNameActive]: enabled,
        [classNameDragging]: dragging,
        [classNameResizing]: resizing,
        [classNameDraggable]: draggable,
        [classNameResizable]: resizable,
      },
      className,
    ]"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @mousedown="elementMouseDown"
    @touchstart="elementTouchDown"
  >
    <div
      v-for="actualHandle in actualHandles"
      :key="actualHandle"
      :class="[classNameHandle, classNameHandle + '-' + actualHandle]"
      :style="{ display: enabled ? 'block' : 'none' }"
      @mousedown.stop.prevent="handleDown(actualHandle, $event)"
      @touchstart.stop.prevent="handleTouchDown(actualHandle, $event)"
    >
      <slot :name="handle" />
    </div>
    <slot />
    <img
      v-if="!hasGhost"
      ref="ghost"
      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFhAJ/wlseKgAAAABJRU5ErkJggg=="
      :width="1"
      :height="1"
    />
  </div>
</template>

<script lang="ts">
// @ts-nocheck
// FIXME: fix console errors
import { onKeyStroke } from "@vueuse/core";
import type { ZIndexProperty } from "csstype";
import { CSSProperties, PropType, type ComponentPublicInstance } from "vue";

import {
  addEvent,
  computeHeight,
  computeWidth,
  getComputedSize,
  matchesSelectorToParentElements,
  pointerPagePosition,
  removeEvent,
  restrictToBounds,
  snapToGrid,
} from "./controllers/draggable-resizable";

type Handle = "tl" | "tm" | "tr" | "mr" | "br" | "bm" | "bl" | "ml";
type Handles = Array<Handle>;
type Bounds = {
  minLeft: null | number;
  maxLeft: null | number;
  minRight: null | number;
  maxRight: null | number;
  minTop: null | number;
  maxTop: null | number;
  minBottom: null | number;
  maxBottom: null | number;
};
type Grid = [x: number, y: number];

const middleHandle = (handle: Handle): Handle => {
  switch (handle) {
    case "tl":
    case "bl":
      return "ml";
    case "tr":
    case "br":
      return "mr";
    default:
      return handle;
  }
};

export type Resize = {
  left: number;
  top: number;
  width: number;
  height: number;
};
const events = {
  mouse: {
    start: "mousedown",
    move: "mousemove",
    stop: "mouseup",
  },
  touch: {
    start: "touchstart",
    move: "touchmove",
    stop: "touchend",
  },
};

const userSelectNone: CSSProperties = {
  userSelect: "none",
  MozUserSelect: "none",
  WebkitUserSelect: "none",
  msUserSelect: "none",
};

let eventsFor = events.mouse;

export default {
  replace: true,
  name: "VueDraggableResizable",
  props: {
    className: {
      type: String,
      default: "vdr",
    },
    classNameDraggable: {
      type: String,
      default: "draggable",
    },
    classNameResizable: {
      type: String,
      default: "resizable",
    },
    classNameDragging: {
      type: String,
      default: "dragging",
    },
    classNameResizing: {
      type: String,
      default: "resizing",
    },
    classNameActive: {
      type: String,
      default: "active",
    },
    classNameHandle: {
      type: String,
      default: "handle",
    },
    disableUserSelect: {
      type: Boolean,
      default: true,
    },
    enableNativeDrag: {
      type: Boolean,
      default: false,
    },
    preventDeactivation: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    draggable: {
      type: Boolean,
      default: true,
    },
    resizable: {
      type: Boolean,
      default: true,
    },
    lockAspectRatio: {
      type: Boolean,
      default: false,
    },
    w: {
      type: [Number, String],
      default: 200,
      validator: (val) => {
        if (typeof val === "number") {
          return val > 0;
        }
        return val === "auto";
      },
    },
    h: {
      type: [Number, String],
      default: 200,
      validator: (val) => {
        if (typeof val === "number") {
          return val > 0;
        }
        return val === "auto";
      },
    },
    minWidth: {
      type: Number,
      default: 0,
      validator: (val: number) => val >= 0,
    },
    minHeight: {
      type: Number,
      default: 0,
      validator: (val: number) => val >= 0,
    },
    maxWidth: {
      type: Number,
      default: null,
      validator: (val: number) => val >= 0,
    },
    maxHeight: {
      type: Number,
      default: null,
      validator: (val: number) => val >= 0,
    },
    x: {
      type: Number,
      default: 0,
    },
    y: {
      type: Number,
      default: 0,
    },
    z: {
      type: [String, Number] as PropType<ZIndexProperty>,
      default: "auto",
      validator: (val: number) =>
        typeof val === "string" ? val === "auto" : val >= 0,
    },
    handles: {
      type: Array as PropType<Handles>,
      default: () => ["tl", "tm", "tr", "mr", "br", "bm", "bl", "ml"],
      validator: (val: Handles) => {
        const s = new Set(["tl", "tm", "tr", "mr", "br", "bm", "bl", "ml"]);
        return new Set(val.filter((h) => s.has(h))).size === val.length;
      },
    },
    dragHandle: {
      type: String,
      default: null,
    },
    dragCancel: {
      type: String,
      default: null,
    },
    axis: {
      type: String,
      default: "both",
      validator: (val: string) => ["x", "y", "both"].includes(val),
    },
    grid: {
      type: Array as unknown as PropType<Grid>,
      default: () => [1, 1],
    },
    parent: {
      type: Boolean,
      default: false,
    },
    scale: {
      type: Number,
      default: 1,
      validator: (val: number) => val > 0,
    },
    onDrag: {
      type: Function,
      default: () => true,
    },
    onResizeStart: {
      type: Function,
      default: () => true,
    },
    onResize: {
      type: Function,
      default: () => true,
    },
    hasGhost: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: {
    activated: () => true,
    deactivated: () => true,
    dragging: (_: boolean) => true,
    init: (_: ComponentPublicInstance) => true,
    "update:active": (_: boolean) => true,
    dragstart: (_: MouseEvent | TouchEvent) => true,
    dragstop: (_left: number, _top: number) => true,
    resizing: (_left: number, _top: number, _width: number, _height: number) =>
      true,
    resizestop: (_data: Resize) => true,
    stoping: (_: ComponentPublicInstance) => true,
  },

  data: function () {
    const handle: Handle | null = null;
    const bounds: Bounds = {
      minLeft: null,
      maxLeft: null,
      minRight: null,
      maxRight: null,
      minTop: null,
      maxTop: null,
      minBottom: null,
      maxBottom: null,
    };
    return {
      isCanceled: false,
      cleanupKeyStrokeListener: null,
      left: this.x,
      top: this.y,
      right: 0,
      bottom: 0,
      width: 0,
      height: 0,
      widthTouched: false,
      heightTouched: false,
      aspectFactor: 0,
      parentWidth: 0,
      parentHeight: 0,
      minW: this.minWidth,
      minH: this.minHeight,
      maxW: this.maxWidth,
      maxH: this.maxHeight,
      handle,
      enabled: this.active,
      resizing: false,
      dragging: false,
      dragEnable: false,
      resizeEnable: false,
      zIndex: this.z,
      mouseClickPosition: {
        mouseX: 0,
        mouseY: 0,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
      },
      bounds,
    };
  },
  computed: {
    style(): CSSProperties {
      return {
        transform: `translate(${this.left}px, ${this.top}px)`,
        width: this.computedWidth,
        height: this.computedHeight,
        zIndex: this.zIndex,
        ...userSelectNone,
      };
    },
    actualHandles(): Handles {
      if (!this.resizable) return [];

      return this.handles;
    },
    computedWidth() {
      if (this.w === "auto") {
        return "auto";
      }
      if (this.width === 0) return "100%";
      return this.width + "px";
    },
    computedHeight() {
      if (this.h === "auto") {
        if (!this.heightTouched) {
          return "auto";
        }
      }

      return this.height + "px";
    },
    resizingOnX() {
      return (
        Boolean(this.handle) &&
        (this.handle?.includes("l") || this.handle?.includes("r"))
      );
    },
    resizingOnY() {
      return (
        Boolean(this.handle) &&
        (this.handle?.includes("t") || this.handle?.includes("b"))
      );
    },
    isCornerHandle() {
      if (!this.handle) return false;
      return (
        Boolean(this.handle) && ["tl", "tr", "br", "bl"].includes(this.handle)
      );
    },
  },

  watch: {
    active(val) {
      this.enabled = val;

      if (val) {
        this.$emit("activated");
      } else {
        this.$emit("deactivated");
      }
    },
    z(val) {
      if (val >= 0 || val === "auto") {
        this.zIndex = val;
      }
    },
    x(val) {
      if (this.resizing || this.dragging) {
        return;
      }

      if (this.parent) {
        this.bounds = this.calcDragLimits();
      }

      this.moveHorizontally(val);
    },
    y(val) {
      if (this.resizing || this.dragging) {
        return;
      }

      if (this.parent) {
        this.bounds = this.calcDragLimits();
      }

      this.moveVertically(val);
    },
    lockAspectRatio(val) {
      if (val) {
        this.aspectFactor = this.width / this.height;
      } else {
        this.aspectFactor = 0;
      }
    },
    minWidth(val) {
      if (val > 0 && val <= this.width) {
        this.minW = val;
      }
    },
    minHeight(val) {
      if (val > 0 && val <= this.height) {
        this.minH = val;
      }
    },
    maxWidth(val) {
      this.maxW = val;
    },
    maxHeight(val) {
      this.maxH = val;
    },
    w(val) {
      if (this.resizing || this.dragging) {
        return;
      }

      if (this.parent) {
        this.bounds = this.calcResizeLimits();
      }

      this.changeWidth(val);
    },
    h(val) {
      if (this.resizing || this.dragging) {
        return;
      }

      if (this.parent) {
        this.bounds = this.calcResizeLimits();
      }

      this.changeHeight(val);
    },
  },
  created: function () {
    if (this.maxWidth && this.minWidth > this.maxWidth)
      // FIXME - log this correctly
      // eslint-disable-next-line
      console.warn(
        "[Vdr warn]: Invalid prop: minWidth cannot be greater than maxWidth"
      );
    if (this.maxWidth && this.minHeight > this.maxHeight)
      // FIXME - log this correctly
      // eslint-disable-next-line
      console.warn(
        "[Vdr warn]: Invalid prop: minHeight cannot be greater than maxHeight"
      );

    this.resetBoundsAndMouseState();
  },
  mounted: function () {
    this.draggableAndResizableInit();

    addEvent(document.documentElement, "mousedown", this.deselect);
    addEvent(document.documentElement, "touchend touchcancel", this.deselect);

    addEvent(window, "resize", this.checkParentSize);
  },
  beforeUnmount: function () {
    this.cleanupKeyStrokeListener?.();
    removeEvent(document.documentElement, "mousedown", this.deselect);
    removeEvent(document.documentElement, "touchstart", this.handleUp);
    removeEvent(document.documentElement, "mousemove", this.move);
    removeEvent(document.documentElement, "touchmove", this.move);
    removeEvent(document.documentElement, "mouseup", this.handleUp);
    removeEvent(
      document.documentElement,
      "touchend touchcancel",
      this.deselect
    );

    removeEvent(window, "resize", this.checkParentSize);
  },

  methods: {
    onDragStart(e) {
      this.removeGhosting(e);
      this.$emit("dragging", true);
    },
    onDragEnd() {
      this.$emit("dragging", false);
    },
    removeGhosting(event) {
      if (this.hasGhost) return;
      if (!(event instanceof MouseEvent)) {
        // FIXME - log this correctly
        // eslint-disable-next-line
        console.info("Parameters must be of type MouseEvent!");
        return;
      }
      if (event.dataTransfer) {
        event.dataTransfer.setDragImage(this.$refs.ghost, 0, 0);
      }
    },
    draggableAndResizableInit(): void {
      this.resetBoundsAndMouseState();

      if (!this.enableNativeDrag) {
        this.$el.ondragstart = () => false;
      }

      const [parentWidth, parentHeight] = this.getParentSize();

      if (parentWidth) this.parentWidth = parentWidth;
      if (parentHeight) this.parentHeight = parentHeight;

      const [width, height] = getComputedSize(this.$el);

      let w = this.w !== "auto" ? this.w : width;
      let h = this.h !== "auto" ? this.h : height;

      if (typeof w === "string") w = parseInt(w);
      if (typeof h === "string") h = parseInt(h);

      this.aspectFactor = w / h;
      this.width = w;
      this.height = h;

      this.$emit("init", this);

      this.right = this.parentWidth - this.width - this.left;
      this.bottom = this.parentHeight - this.height - this.top;

      if (this.active) {
        this.$emit("activated");
      }
    },
    resetBoundsAndMouseState(): void {
      this.mouseClickPosition = {
        mouseX: 0,
        mouseY: 0,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
      };

      this.bounds = {
        minLeft: 0,
        maxLeft: 0,
        minRight: 0,
        maxRight: 0,
        minTop: 0,
        maxTop: 0,
        minBottom: 0,
        maxBottom: 0,
      };
    },
    checkParentSize(): void {
      if (this.parent) {
        const [newParentWidth, newParentHeight] = this.getParentSize();

        if (newParentWidth) this.parentWidth = newParentWidth;
        if (newParentHeight) this.parentHeight = newParentHeight;
        this.right = this.parentWidth - this.width - this.left;
        this.bottom = this.parentHeight - this.height - this.top;
      }
    },
    getParentSize(): null[] | number[] {
      if (this.parent) {
        const style = window.getComputedStyle(this.$el.parentNode, null);

        return [
          parseInt(style.getPropertyValue("width"), 10),
          parseInt(style.getPropertyValue("height"), 10),
        ];
      }

      return [null, null];
    },
    elementTouchDown(e: MouseEvent | TouchEvent): void {
      eventsFor = events.touch;

      this.elementDown(e);
    },
    elementMouseDown(e: MouseEvent | TouchEvent): void {
      eventsFor = events.mouse;

      this.elementDown(e);
    },
    elementDown(e: MouseEvent | TouchEvent): void {
      if (e instanceof MouseEvent && e.which !== 1) {
        return;
      }

      const target = e.target;

      if (!this.$el.contains(target) || !this.onDragStart(e)) return;

      if (
        (this.dragHandle &&
          !matchesSelectorToParentElements(
            target,
            this.dragHandle,
            this.$el
          )) ||
        (this.dragCancel &&
          matchesSelectorToParentElements(target, this.dragCancel, this.$el))
      ) {
        this.dragging = false;
        return;
      }

      if (!this.enabled) {
        this.enabled = true;
        this.$emit("activated");
        this.$emit("update:active", true);
      }

      if (this.draggable) {
        this.dragEnable = true;
      }

      const { pageX, pageY } = pointerPagePosition(e);
      this.mouseClickPosition.mouseX = pageX;
      this.mouseClickPosition.mouseY = pageY;
      this.mouseClickPosition.left = this.left;
      this.mouseClickPosition.right = this.right;
      this.mouseClickPosition.top = this.top;
      this.mouseClickPosition.bottom = this.bottom;

      if (this.parent) {
        this.bounds = this.calcDragLimits();
      }

      addEvent(document.documentElement, eventsFor.move, this.move);
      addEvent(document.documentElement, eventsFor.stop, this.handleUp);
    },
    calcDragLimits(): Bounds {
      return {
        minLeft: this.left % this.grid[0],
        maxLeft:
          Math.floor(
            (this.parentWidth - this.width - this.left) / this.grid[0]
          ) *
            this.grid[0] +
          this.left,
        minRight: this.right % this.grid[0],
        maxRight:
          Math.floor(
            (this.parentWidth - this.width - this.right) / this.grid[0]
          ) *
            this.grid[0] +
          this.right,
        minTop: this.top % this.grid[1],
        maxTop:
          Math.floor(
            (this.parentHeight - this.height - this.top) / this.grid[1]
          ) *
            this.grid[1] +
          this.top,
        minBottom: this.bottom % this.grid[1],
        maxBottom:
          Math.floor(
            (this.parentHeight - this.height - this.bottom) / this.grid[1]
          ) *
            this.grid[1] +
          this.bottom,
      };
    },
    deselect(e: MouseEvent | TouchEvent): void {
      const target = e.target;
      const isInside = target instanceof Node && this.$el.contains(target);
      const targetClassName =
        target instanceof HTMLElement
          ? target.className
          : target instanceof SVGElement
            ? String(target.className)
            : "";
      const regex = new RegExp(this.className + "-([trmbl]{2})", "");

      if (!isInside && !regex.test(targetClassName)) {
        if (this.enabled && !this.preventDeactivation) {
          this.enabled = false;

          this.$emit("deactivated");
          this.$emit("update:active", false);
        }

        removeEvent(
          document.documentElement,
          eventsFor.move,
          this.handleResize
        );
      }

      this.resetBoundsAndMouseState();
    },
    handleTouchDown(handle: Handle, e: MouseEvent | TouchEvent): void {
      eventsFor = events.touch;

      this.handleDown(handle, e);
    },
    handleDown(handle: Handle, e: MouseEvent | TouchEvent): void {
      this.cleanupKeyStrokeListener = onKeyStroke(
        "Escape",
        (e) => {
          e.preventDefault();
          if (this.resizing) {
            const mouseUpEvent = new MouseEvent("mouseup", {
              bubbles: true,
              cancelable: true,
            });
            document.dispatchEvent(mouseUpEvent);
            this.isCanceled = true;
            this.handleUp();
          }
          this.cleanupKeyStrokeListener(); // This will unregister the listener
        },
        { dedupe: true }
      );
      if (e instanceof MouseEvent && e.which !== 1) {
        return;
      }

      if (this.onResizeStart(handle, e) === false) {
        return;
      }

      if (e.stopPropagation) e.stopPropagation();

      // Here we avoid a dangerous recursion by faking
      // corner handles as middle handles
      if (this.lockAspectRatio && !handle.includes("m")) {
        this.handle = middleHandle(handle);
      } else {
        this.handle = handle;
      }

      this.resizeEnable = true;

      const { pageX, pageY } = pointerPagePosition(e);
      this.mouseClickPosition.mouseX = pageX;
      this.mouseClickPosition.mouseY = pageY;
      this.mouseClickPosition.left = this.left;
      this.mouseClickPosition.right = this.right;
      this.mouseClickPosition.top = this.top;
      this.mouseClickPosition.bottom = this.bottom;

      this.bounds = this.calcResizeLimits();

      addEvent(document.documentElement, eventsFor.move, this.handleResize);
      addEvent(document.documentElement, eventsFor.stop, this.handleUp);
    },
    calcResizeLimits(): Bounds {
      let minW = this.minW;
      let minH = this.minH;
      let maxW = this.maxW;
      let maxH = this.maxH;

      const aspectFactor = this.aspectFactor;
      const [gridX, gridY] = this.grid;
      const width = this.width;
      const height = this.height;
      const left = this.left;
      const top = this.top;
      const right = this.right;
      const bottom = this.bottom;

      if (this.lockAspectRatio) {
        if (minW / minH > aspectFactor) {
          minH = minW / aspectFactor;
        } else {
          minW = aspectFactor * minH;
        }

        if (maxW && maxH) {
          maxW = Math.min(maxW, aspectFactor * maxH);
          maxH = Math.min(maxH, maxW / aspectFactor);
        } else if (maxW) {
          maxH = maxW / aspectFactor;
        } else if (maxH) {
          maxW = aspectFactor * maxH;
        }
      }

      maxW = maxW - (maxW % gridX);
      maxH = maxH - (maxH % gridY);

      const limits: Bounds = {
        minLeft: null,
        maxLeft: null,
        minTop: null,
        maxTop: null,
        minRight: null,
        maxRight: null,
        minBottom: null,
        maxBottom: null,
      };

      if (this.parent) {
        limits.minLeft = left % gridX;
        limits.maxLeft = left + Math.floor((width - minW) / gridX) * gridX;
        limits.minTop = top % gridY;
        limits.maxTop = top + Math.floor((height - minH) / gridY) * gridY;
        limits.minRight = right % gridX;
        limits.maxRight = right + Math.floor((width - minW) / gridX) * gridX;
        limits.minBottom = bottom % gridY;
        limits.maxBottom = bottom + Math.floor((height - minH) / gridY) * gridY;

        if (maxW) {
          limits.minLeft = Math.max(
            limits.minLeft,
            this.parentWidth - right - maxW
          );
          limits.minRight = Math.max(
            limits.minRight,
            this.parentWidth - left - maxW
          );
        }

        if (maxH) {
          limits.minTop = Math.max(
            limits.minTop,
            this.parentHeight - bottom - maxH
          );
          limits.minBottom = Math.max(
            limits.minBottom,
            this.parentHeight - top - maxH
          );
        }

        if (this.lockAspectRatio) {
          limits.minLeft = Math.max(limits.minLeft, left - top * aspectFactor);
          limits.minTop = Math.max(limits.minTop, top - left / aspectFactor);
          limits.minRight = Math.max(
            limits.minRight,
            right - bottom * aspectFactor
          );
          limits.minBottom = Math.max(
            limits.minBottom,
            bottom - right / aspectFactor
          );
        }
      } else {
        limits.minLeft = null;
        limits.maxLeft = left + Math.floor((width - minW) / gridX) * gridX;
        limits.minTop = null;
        limits.maxTop = top + Math.floor((height - minH) / gridY) * gridY;
        limits.minRight = null;
        limits.maxRight = right + Math.floor((width - minW) / gridX) * gridX;
        limits.minBottom = null;
        limits.maxBottom = bottom + Math.floor((height - minH) / gridY) * gridY;

        if (maxW) {
          limits.minLeft = -(right + maxW);
          limits.minRight = -(left + maxW);
        }

        if (maxH) {
          limits.minTop = -(bottom + maxH);
          limits.minBottom = -(top + maxH);
        }

        if (this.lockAspectRatio && maxW && maxH) {
          if (limits.minLeft)
            limits.minLeft = Math.min(limits.minLeft, -(right + maxW));
          if (limits.minTop)
            limits.minTop = Math.min(limits.minTop, -(maxH + bottom));
          if (limits.minRight)
            limits.minRight = Math.min(limits.minRight, -left - maxW);
          if (limits.minBottom)
            limits.minBottom = Math.min(limits.minBottom, -top - maxH);
        }
      }

      return limits;
    },
    move(e: MouseEvent | TouchEvent): void {
      if (this.resizing) {
        this.handleResize(e);
      } else if (this.dragEnable) {
        this.handleDrag(e);
      }
    },
    handleDrag(e: MouseEvent | TouchEvent): void {
      const axis = this.axis;
      const grid = this.grid;

      const bounds = this.bounds;

      const mouseClickPosition = this.mouseClickPosition;

      const { pageX, pageY } = pointerPagePosition(e);
      const tmpDeltaX =
        axis && axis !== "y" ? mouseClickPosition.mouseX - pageX : 0;
      const tmpDeltaY =
        axis && axis !== "x" ? mouseClickPosition.mouseY - pageY : 0;

      const [deltaX, deltaY] = snapToGrid(
        grid,
        tmpDeltaX,
        tmpDeltaY,
        this.scale
      );

      const left = restrictToBounds(
        mouseClickPosition.left - deltaX,
        bounds.minLeft,
        bounds.maxLeft
      );
      const top = restrictToBounds(
        mouseClickPosition.top - deltaY,
        bounds.minTop,
        bounds.maxTop
      );

      if (this.onDrag(left, top) === false) {
        return;
      }

      const right = restrictToBounds(
        mouseClickPosition.right + deltaX,
        bounds.minRight,
        bounds.maxRight
      );
      const bottom = restrictToBounds(
        mouseClickPosition.bottom + deltaY,
        bounds.minBottom,
        bounds.maxBottom
      );

      this.left = left;
      this.top = top;
      this.right = right;
      this.bottom = bottom;

      // this.$emit("dragging", this.left, this.top);
      this.$emit("dragstart", e);

      this.dragging = true;
    },
    moveHorizontally(val: number): void {
      // should calculate with scale 1.
      const [deltaX, _] = snapToGrid(this.grid, val, this.top, 1);

      const left = restrictToBounds(
        deltaX,
        this.bounds.minLeft,
        this.bounds.maxLeft
      );

      this.left = left;
      this.right = this.parentWidth - this.width - left;
    },
    moveVertically(val: number): void {
      // should calculate with scale 1.
      const [_, deltaY] = snapToGrid(this.grid, this.left, val, 1);

      const top = restrictToBounds(
        deltaY,
        this.bounds.minTop,
        this.bounds.maxTop
      );

      this.top = top;
      this.bottom = this.parentHeight - this.height - top;
    },
    handleResize(e: MouseEvent | TouchEvent): void {
      let left = this.left;
      let top = this.top;
      let right = this.right;
      let bottom = this.bottom;

      const mouseClickPosition = this.mouseClickPosition;
      const aspectFactor = this.aspectFactor;

      const { pageX, pageY } = pointerPagePosition(e);
      const tmpDeltaX = mouseClickPosition.mouseX - pageX;
      const tmpDeltaY = mouseClickPosition.mouseY - pageY;

      if (!this.widthTouched && tmpDeltaX) {
        this.widthTouched = true;
      }

      if (!this.heightTouched && tmpDeltaY) {
        this.heightTouched = true;
      }

      const [deltaX, deltaY] = snapToGrid(
        this.grid,
        tmpDeltaX,
        tmpDeltaY,
        this.scale
      );

      if (this.handle?.includes("b")) {
        bottom = restrictToBounds(
          mouseClickPosition.bottom + deltaY,
          this.bounds.minBottom,
          this.bounds.maxBottom
        );

        if (this.lockAspectRatio && this.resizingOnY) {
          right = this.right - (this.bottom - bottom) * aspectFactor;
        }
      } else if (this.handle?.includes("t")) {
        top = restrictToBounds(
          mouseClickPosition.top - deltaY,
          this.bounds.minTop,
          this.bounds.maxTop
        );

        if (this.lockAspectRatio && this.resizingOnY) {
          left = this.left - (this.top - top) * aspectFactor;
        }
      }

      if (this.handle?.includes("r")) {
        right = restrictToBounds(
          mouseClickPosition.right + deltaX,
          this.bounds.minRight,
          this.bounds.maxRight
        );

        if (this.lockAspectRatio && this.resizingOnX) {
          bottom = this.bottom - (this.right - right) / aspectFactor;
        }
      } else if (this.handle?.includes("l")) {
        left = restrictToBounds(
          mouseClickPosition.left - deltaX,
          this.bounds.minLeft,
          this.bounds.maxLeft
        );

        if (this.lockAspectRatio && this.resizingOnX) {
          top = this.top - (this.left - left) / aspectFactor;
        }
      }

      const width = computeWidth(this.parentWidth, left, right);
      const height = computeHeight(this.parentHeight, top, bottom);

      if (this.onResize(this.handle, left, top, width, height) === false) {
        return;
      }

      this.left = left;
      this.top = top;
      this.right = right;
      this.bottom = bottom;
      this.width = width;
      this.height = height;

      this.$emit("resizing", this.left, this.top, this.width, this.height);

      this.resizing = true;
    },
    changeWidth(val: number): void {
      // should calculate with scale 1.
      const [newWidth, _] = snapToGrid(this.grid, val, 0, 1);

      let right = restrictToBounds(
        this.parentWidth - newWidth - this.left,
        this.bounds.minRight,
        this.bounds.maxRight
      );
      let bottom = this.bottom;

      if (this.lockAspectRatio) {
        bottom = this.bottom - (this.right - right) / this.aspectFactor;
      }

      const width = computeWidth(this.parentWidth, this.left, right);
      const height = computeHeight(this.parentHeight, this.top, bottom);

      this.right = right;
      this.bottom = bottom;
      this.width = width;
      this.height = height;
    },
    changeHeight(val: number): void {
      // should calculate with scale 1.
      const [_, newHeight] = snapToGrid(this.grid, 0, val, 1);

      let bottom = restrictToBounds(
        this.parentHeight - newHeight - this.top,
        this.bounds.minBottom,
        this.bounds.maxBottom
      );
      let right = this.right;

      if (this.lockAspectRatio) {
        right = this.right - (this.bottom - bottom) * this.aspectFactor;
      }

      const width = computeWidth(this.parentWidth, this.left, right);
      const height = computeHeight(this.parentHeight, this.top, bottom);

      this.right = right;
      this.bottom = bottom;
      this.width = width;
      this.height = height;
    },
    handleUp(): void {
      this.handle = null;
      this.cleanupKeyStrokeListener();
      this.resetBoundsAndMouseState();

      this.dragEnable = false;
      this.resizeEnable = false;

      if (this.resizing) {
        this.resizing = false;
        if (this.isCanceled) this.$emit("resizestop", null);
        else
          this.$emit("resizestop", {
            left: this.left,
            top: this.top,
            width: this.width,
            height: this.height,
          });
      }

      if (this.dragging) {
        this.dragging = false;
        this.$emit("dragstop", this.left, this.top);
      }

      this.$emit("stoping", this);

      removeEvent(document.documentElement, eventsFor.move, this.handleResize);
    },
  },
};
</script>

<style lang="scss" scoped>
$point: -6px;
$point-position: -4px;
$point-space: 10px;
$point-color: #333;

.vdr {
  touch-action: none;
  position: absolute;
  box-sizing: border-box;
}
.handle {
  box-sizing: border-box;
  position: absolute;
  width: $point-space;
  height: $point-space;
}
.handle-tl {
  top: $point;
  inset-inline-start: $point;
  cursor: nw-resize;
}
.handle-tm {
  top: $point;
  inset-inline-start: 50%;
  margin-inline-start: $point-position;
  cursor: n-resize;
}
.handle-tr {
  top: $point;
  inset-inline-end: $point;
  cursor: ne-resize;
}
.handle-ml {
  top: 50%;
  margin-top: $point-position;
  inset-inline-start: $point;
  cursor: w-resize;
}
.handle-mr {
  top: 50%;
  margin-top: $point-position;
  inset-inline-end: $point;
  cursor: e-resize;
}
.handle-bl {
  bottom: $point;
  inset-inline-start: $point;
  cursor: sw-resize;
}
.handle-bm {
  bottom: $point;
  inset-inline-start: 50%;
  margin-inline-start: $point-position;
  cursor: s-resize;
}
.handle-br {
  bottom: $point;
  inset-inline-end: $point;
  cursor: se-resize;
}
@media only screen and (max-width: 768px) {
  [class*="handle-"]:before {
    content: "";
    inset-inline-start: $point;
    inset-inline-end: $point;
    bottom: $point;
    top: $point;
    position: absolute;
  }
}
</style>
